import React, { useState, useEffect } from 'react';
import { fetchPlatformStats, PlatformStats as PlatformStatsType } from '../../services/platformStatsService';
import styles from './PlatformStats.module.css';

interface StatCardProps {
  title: string;
  count: number;
  icon: string;
  color: string;
  description: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, count, icon, color, description }) => {
  return (
    <div className={styles.statCard}>
      <div className={styles.statHeader}>
        <div className={styles.statIcon}>
          {icon}
        </div>
        <div className={styles.statNumber}>{count.toLocaleString()}</div>
      </div>
      <div className={styles.statContent}>
        <div className={styles.statTitle}>{title}</div>
        <div className={styles.statDescription}>{description}</div>
      </div>
    </div>
  );
};

const PlatformStats: React.FC = () => {
  const [stats, setStats] = useState<PlatformStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await fetchPlatformStats();
        setStats(data);
      } catch (err) {
        setError('Failed to load platform statistics');
        console.error('Error loading platform stats:', err);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading community stats...</div>
      </div>
    );
  }

  if (error || !stats) {
    return null; // Fail silently for now
  }

  // Format dietary preference data
  const dietaryPreferenceLabels: { [key: string]: string } = {
    'users_with_no_diet': 'No Diet',
    'vegan': 'Vegans',
    'vegetarian': 'Vegetarians', 
    'pescatarian': 'Pescatarians',
    'users_with_allergies': 'Other Allergies',
    'low_fodmap': 'Low FODMAP',
    'keto': 'Keto',
    'paleo': 'Paleo',
    'atkins': 'Atkins'
  };

  const dietaryPreferenceIcons: { [key: string]: string } = {
    'users_with_no_diet': 'N',
    'vegan': 'V',
    'vegetarian': 'V',
    'pescatarian': 'P',
    'users_with_allergies': 'A',
    'low_fodmap': 'LF',
    'keto': 'K',
    'paleo': 'P',
    'atkins': 'A'
  };

  // Use consistent color for all dietary preference cards
  const dietaryPreferenceColor = '#4ecdc4'; // Teal color

  // Get all dietary preferences sorted by count, with specific positioning
  const allPreferences = Object.entries(stats.dietary_preferences)
    .sort(([keyA, countA], [keyB, countB]) => {
      // Put "users_with_no_diet" first (after Total Users)
      if (keyA === 'users_with_no_diet') return -1;
      if (keyB === 'users_with_no_diet') return 1;
      // Put "users_with_allergies" at the end regardless of count
      if (keyA === 'users_with_allergies') return 1;
      if (keyB === 'users_with_allergies') return -1;
      // Sort others by count (highest first)
      return countB - countA;
    });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Our Community</h2>
        <p className={styles.subtitle}>
          Join {stats.total_users.toLocaleString()} people who are taking control of their dietary preferences
        </p>
        <p className={styles.communityLabel}>Community members by dietary preference:</p>
      </div>
      
      <div className={styles.statsGrid}>
        {/* Total Users */}
        <StatCard
          title="Total Users"
          count={stats.total_users}
          icon="U"
          color="#4ecdc4"
          description="on Platefull AI"
        />
        
        {/* All dietary preferences */}
        {allPreferences.map(([preference, count]) => (
          <StatCard
            key={preference}
            title={dietaryPreferenceLabels[preference] || preference}
            count={count}
            icon={dietaryPreferenceIcons[preference] || 'D'}
            color={dietaryPreferenceColor}
            description=""
          />
        ))}
      </div>
    </div>
  );
};

export default PlatformStats;
